/**
 * "Screening links" — the folder on top of Interview Links (Sofia, Oct 24 2026).
 *
 * One link per TA manager for a first "Kennenlernen" a prospect books from a LinkedIn InMail
 * (proxy-server/lib/screeningLinks.js). Everyone's are listed so the team can see and copy them;
 * only the owner edits or closes their own. The page makes the viewer's link on open; the button
 * here is for when it was closed (or that failed).
 */
import type { Ref } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Loader2, Pencil, Plus, Power } from 'lucide-react';
import type { InterviewLink } from '../../services/interviewLinksApi';
import { availabilitySummary } from './availabilitySummary';
import { LinkedInLinkActions } from './LinkedInLinkActions';
import { personLabel } from './personLabel';

export function ScreeningLinks({
  ref,
  links,
  me,
  open,
  creating,
  busyId,
  onToggle,
  onCreate,
  onEdit,
  onDeactivate,
  onReopen,
}: {
  ref?: Ref<HTMLDetailsElement>;
  links: InterviewLink[];
  me: string;
  open: boolean;
  creating: boolean;
  busyId: string | null;
  onToggle: (open: boolean) => void;
  onCreate: () => void;
  onEdit: (link: InterviewLink) => void;
  onDeactivate: (link: InterviewLink) => void;
  onReopen: (link: InterviewLink) => void;
}) {
  const mine = (l: InterviewLink) => (l.taOwner || '').toLowerCase() === me;
  // Mine first, then by person.
  const sorted = [...links].sort(
    (a, b) => Number(mine(b)) - Number(mine(a)) || personLabel(a.taOwner).localeCompare(personLabel(b.taOwner))
  );
  const hasMine = links.some((l) => mine(l) && l.active);

  return (
    <details
      ref={ref}
      id="screening-links"
      open={open}
      onToggle={(e) => onToggle((e.currentTarget as HTMLDetailsElement).open)}
      className="mb-4 rounded-2xl border border-gray-100 bg-white p-4 shadow-sm"
    >
      <summary className="cursor-pointer text-sm font-semibold text-gray-900">
        Screening links ({links.filter((l) => l.active).length})
      </summary>
      <p className="mt-1 text-xs text-gray-500">
        One per TA manager: a first call candidates book from your LinkedIn InMail. It belongs to no
        role, and a booking writes nothing to Lever. Your InMails use yours as the booking link.
      </p>

      {!hasMine ? (
        <Button size="sm" className="mt-3" onClick={onCreate} disabled={creating}>
          {creating ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1.5 h-3.5 w-3.5" />}
          Create my screening link
        </Button>
      ) : null}

      <div className="mt-3 divide-y divide-gray-100">
        {sorted.map((link) => (
          <div key={link.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-gray-900">{personLabel(link.taOwner)}</span>
                {mine(link) ? (
                  <span className="rounded bg-[#DBEBFC] px-2 py-0.5 text-[11px] font-medium text-[#0C447C]">You</span>
                ) : null}
                {!link.active ? (
                  <span className="rounded bg-[#FCDFE4] px-2 py-0.5 text-[11px] font-medium text-[#791F1F]">Closed</span>
                ) : null}
              </div>
              <p className="mt-0.5 text-xs text-gray-500">
                {link.minutes} min · {availabilitySummary(link.interviewers?.[0])}
              </p>
              {link.active && link.shareUrl ? (
                <p className="mt-0.5 break-all text-xs text-gray-500">{link.shareUrl}</p>
              ) : null}
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-1.5">
              <LinkedInLinkActions link={link} />
              {mine(link) ? (
                <>
                  <Button size="sm" variant="ghost" onClick={() => onEdit(link)}>
                    <Pencil className="mr-1.5 h-3.5 w-3.5" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => (link.active ? onDeactivate(link) : onReopen(link))}
                    disabled={busyId === link.id}
                  >
                    <Power className="mr-1.5 h-3.5 w-3.5" />
                    {link.active ? 'Close' : 'Reopen'}
                  </Button>
                </>
              ) : null}
            </div>
          </div>
        ))}
        {!sorted.length ? <p className="py-3 text-xs text-gray-500">No screening links yet.</p> : null}
      </div>
    </details>
  );
}
